import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import type { ChangeEvent, CompositionEvent, FocusEvent, KeyboardEvent } from "react";
import { correctAtCaret } from "./orthography";
import type { Correction } from "./orthography";
import { reviewPunctuation } from "./punctuation";
import type { WritingReview, PunctuationSuggestion } from "./punctuation";

const KEY = "omnisynia.orthography.enabled.v1";
const EVENT = "omnisynia:orthography-change";
let sessionPreference = true;

function getEnabled() {
  try { return window.localStorage.getItem(KEY) !== "false"; } catch { return sessionPreference; }
}
function subscribe(listener: () => void) {
  window.addEventListener(EVENT, listener);
  window.addEventListener("storage", listener);
  return () => { window.removeEventListener(EVENT, listener); window.removeEventListener("storage", listener); };
}
export function useAutocorrectionPreference() {
  const enabled = useSyncExternalStore(subscribe, getEnabled, () => true);
  return [enabled, (next: boolean) => {
    sessionPreference = next;
    try { window.localStorage.setItem(KEY, String(next)); } catch { /* no draft persistence */ }
    window.dispatchEvent(new CustomEvent(EVENT));
  }] as const;
}

function documentLanguage() {
  return typeof document === "undefined" ? "und" : document.documentElement.lang || navigator.language || "und";
}
type InputElement = HTMLTextAreaElement | HTMLInputElement;
export interface AutocorrectionInputOptions {
  value: string;
  onValueChange: (value: string) => void;
  language?: string;
  enabled: boolean;
  protectedWords?: readonly string[];
  maxLength?: number;
  /** Use false for code, names, credentials, exact quotations or identifiers. */
  naturalLanguage?: boolean;
  /** Disable sentence punctuation for titles and other short labels. */
  sentenceStructure?: boolean;
}

/** Reuse in every natural-language input. Does not touch persisted messages or call a server. */
export function useAutocorrectedInput(options: AutocorrectionInputOptions) {
  const { value, onValueChange, enabled, protectedWords = [], maxLength, naturalLanguage = true } = options;
  const language = options.language || documentLanguage();
  const ref = useRef<InputElement | null>(null);
  const composing = useRef(false);
  const pendingSelection = useRef<{ value: string; caret: number } | null>(null);
  const ignored = useRef(new Set<string>());
  const [lastCorrection, setLastCorrection] = useState<(Correction & { kind?: "punctuation" }) | null>(null);
  const [review, setReview] = useState<WritingReview | null>(null);
  const suppressedReview = useRef<string | null>(null);
  const active = enabled && naturalLanguage;

  useEffect(() => { setReview(null); }, [language, active, options.sentenceStructure]);

  useLayoutEffect(() => {
    const pending = pendingSelection.current;
    pendingSelection.current = null;
    if (pending && pending.value === value && ref.current === document.activeElement)
      ref.current?.setSelectionRange(pending.caret, pending.caret);
  }, [value]);

  useEffect(() => {
    if (lastCorrection && lastCorrection.after !== value) setLastCorrection(null);
    if (review && review.text !== value) setReview(null);
    if (!value) { ignored.current.clear(); suppressedReview.current = null; }
  }, [value, lastCorrection, review]);

  function apply(text: string, caret: number, boundary: boolean) {
    const correction = correctAtCaret(text, caret, {
      enabled: active, language, maxLength, requireBoundary: boundary,
      protectedWords: [...protectedWords, ...ignored.current]
    });
    if (correction) {
      pendingSelection.current = { value: correction.after, caret: correction.caretAfter };
      setLastCorrection(correction);
      onValueChange(correction.after);
    } else onValueChange(text);
  }

  function undo() {
    if (!lastCorrection || value !== lastCorrection.after) return;
    if (lastCorrection.kind === "punctuation") suppressedReview.current = lastCorrection.before;
    else ignored.current.add(lastCorrection.from.toLocaleLowerCase());
    pendingSelection.current = { value: lastCorrection.before, caret: lastCorrection.caretBefore };
    onValueChange(lastCorrection.before);
    setLastCorrection(null);
    setReview(null);
  }

  function applySuggestion(suggestion: PunctuationSuggestion) {
    if (!active || !review || review.text !== value || !review.suggestions.includes(suggestion)) return;
    if (maxLength !== undefined && suggestion.text.length > maxLength) return;
    setLastCorrection({before:value,after:suggestion.text,from:value,to:suggestion.text,
      caretBefore:value.length,caretAfter:suggestion.text.length,kind:"punctuation"});
    onValueChange(suggestion.text);
    setReview(null);
  }

  function onChange(event: ChangeEvent<InputElement>) {
    const input = event.currentTarget;
    const native = event.nativeEvent as InputEvent;
    if (composing.current || native.isComposing || !["insertText", "insertLineBreak"].includes(native.inputType) ||
        input.selectionStart !== input.selectionEnd || input.selectionStart === null) {
      onValueChange(input.value);
      return;
    }
    apply(input.value, input.selectionStart, true);
  }
  function onBlur(event: FocusEvent<InputElement>) {
    // Only finish a word at the end; no changes to a selected or mid-sentence range.
    if (!composing.current && event.currentTarget.selectionStart === value.length &&
        event.currentTarget.selectionEnd === value.length) {
      const spelling = correctAtCaret(value,value.length,{enabled:active,language,maxLength,
        requireBoundary:false,protectedWords:[...protectedWords,...ignored.current]});
      const base=spelling?.after ?? value;
      const nextReview=active && options.sentenceStructure !== false && value !== suppressedReview.current
        ? reviewPunctuation(base,language,maxLength) : null;
      const after=nextReview?.text ?? base;
      setReview(nextReview);
      if(after!==value) {
        const punctuation=after!==base;
        setLastCorrection({before:value,after,from:spelling?.from ?? value,to:spelling?.to ?? after,
          caretBefore:value.length,caretAfter:after.length,...(punctuation?{kind:"punctuation" as const}:{})});
        onValueChange(after);
      }
    }
  }
  function onKeyDown(event: KeyboardEvent<InputElement>) {
    if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey &&
        event.key.toLowerCase() === "z" && lastCorrection?.after === value) {
      event.preventDefault(); undo();
    }
  }
  return {
    lastCorrection, undo, review, applySuggestion,
    props: {
      ref: (element: InputElement | null) => { ref.current = element; },
      onChange, onBlur, onKeyDown,
      onCompositionStart: () => { composing.current = true; },
      onCompositionEnd: (event: CompositionEvent<InputElement>) => {
        composing.current = false; onValueChange(event.currentTarget.value);
      },
      lang: language,
      spellCheck: active,
      // The local engine owns replacements; browser spellcheck remains a source of suggestions.
      autoCorrect: "off",
      autoCapitalize: naturalLanguage ? "sentences" : "off"
    }
  };
}

export function WritingSuggestions({ review, onApply }: {
  review: WritingReview | null;
  onApply: (suggestion: PunctuationSuggestion) => void;
}) {
  if (!review || (!review.issues.length && !review.suggestions.length)) return null;
  return <div className="chat-writing-review" aria-label="Revisión de puntuación">
    {Array.from(new Set(review.issues)).map(issue => <p key={issue} role="status">{issue}</p>)}
    {review.suggestions.slice(0, 2).map(suggestion => <div key={suggestion.id}>
      <p style={{whiteSpace:"pre-wrap"}}>{suggestion.text}</p>
      <p><small>{suggestion.explanation}</small></p>
      <button type="button" className="secondary-button" onClick={() => onApply(suggestion)}>
        {suggestion.label}
      </button>
    </div>)}
  </div>;
}
